import '../../styles/HuntsPage.css'
import '../../styles/Tables.css'

import React, { useContext, useEffect } from 'react';
import { useTable, useSortBy, useFilters, useGlobalFilter } from 'react-table';
import { useTranslation } from "react-i18next";

import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

import { SkeletonTable } from "@nybble-security/nybble-react-library";
import { dateDisplay, formatDuration, priorityTranslator } from '../../utils/formatDate';
import { DefaultColumnFilter, SelectColumnFilter, SelectPriorityColumnFilter } from '../../components/TableFilters';
import { useQuery } from '@tanstack/react-query';
import { ResponseSnackbarErrorHandler } from '../../components/ResponseSnackbar';
import { UserInfosContext } from '../../context/UserInfosContext';
import { useAuth0 } from '@auth0/auth0-react';
import { getClientAlerts } from '../../services/alert.service';
import { TextField } from '@mui/material';


function ClientAlerts({ clientId, period }) {
  const { setSnackbarObj } = useContext(UserInfosContext);

  // react query to fetch client alerts for a specific period
  const { getAccessTokenSilently } = useAuth0();
  const {
    data: alerts,
    isLoading: loadingClientAlerts,
    error: errorClientAlerts,
    failureReason: failureClientAlerts,
    failureCount: failureCountClientAlerts,
    isFetching: fetchingClientAlerts
  } = useQuery({
    enabled: !!getAccessTokenSilently && !!clientId && !!period, // ensures the query is only executed when these conditions are met.
    queryKey: ['clientAlerts', clientId, period],
    queryFn: () => getAccessTokenSilently().then((token) => getClientAlerts(token, clientId, period)),
  });

  useEffect(() => {
    const snackbarClientInfos = ResponseSnackbarErrorHandler('clientAlerts', errorClientAlerts, failureClientAlerts, failureCountClientAlerts)
    if (snackbarClientInfos) { setSnackbarObj(snackbarClientInfos) }

  }, [errorClientAlerts, failureClientAlerts, failureCountClientAlerts, setSnackbarObj]);

  return (
    <ClientAlertsTable
      alerts={alerts}
      loadingData={loadingClientAlerts && fetchingClientAlerts} // replace with actual loading state if needed
    />
  )
}

function ClientAlertsTable({ alerts, loadingData }) {
  const { t } = useTranslation();

  const headers = [
    { header: t('tables.header-CreatedDate'), enableSorting: true },
    { header: t('tables.header-Name'), enableColumnFilter: true },
    { header: t('tables.header-Severity'), enableColumnFilter: true },
    { header: t('tables.header-Status') },
    { header: t('tables.header-Decision'), width: '140px' },
    { header: t('tables.header-Duration'), enableSorting: true },
  ]

  if (loadingData) return (
    <div className='tableFixHead report-table'>
      <SkeletonTable columns={headers} rows={3} />
    </div>
  )

  if (!alerts || alerts.length === 0) {
    return <p>{t('adminPages.clientAlertsTable.info-noData')}</p>
  } else {
    return (<Table data={alerts} />);
  }
}


function Table({ data }) {
  const { t } = useTranslation();

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const TextWithLineBreaks = ({ text }) => {
    if (!text) return null; // Handle empty or undefined text
    return (
      <>
        {text.split('\\n').map((line, index) => (
          <React.Fragment key={index}>
            {line}
            <br />
          </React.Fragment>
        ))}
      </>
    );
  };


  const RenderDecision = ({ value }) => {
    if (!value) return null; // Handle empty or undefined value
    return (
      <>
        <u>Context:</u><br/><TextWithLineBreaks text={value?.contexte} /><br/>
        <u>Impact Potentiel:</u><br/><TextWithLineBreaks text={value?.impact_potentiel} />
      </>
    );
  }

  const DisplaySLA = ({ alert }) => {
    // calcul de l'elapsed time
    // cas 1 : alert Opened -> prendre now - alert.createdAt
    if (!alert) return null;

    const startDate = new Date(alert.createdAt);
    const endDate = (alert.status === 'Opened') ? new Date() : new Date(alert.history.find(action => action.action === 'close').timestamp)

    const duration = endDate - startDate;
    //check if endDate is after alert.sla_expiration
    return (
      <>
        {endDate > new Date(alert.sla_expiration)
          ? (
            <p style={{ color: 'var(--nhub-color-secondary-1)', whiteSpace: 'nowrap' }}>
              {formatDuration(duration)}
              <br />
              SLA dépassé de {formatDuration(Math.abs(endDate - new Date(alert.sla_expiration)))}
            </p>)
          :
          (<p>
            {formatDuration(duration)}
          </p>)
        }
      </>
    )
  }



  const columns = React.useMemo(
    () => [
      {
        Header: t('tables.header-CreatedDate'),
        accessor: 'createdAt',
        canSorter: true,
        Filter: false,
        Cell: ({ row, cell: { value } }) => <>{dateDisplay(value)}</>,

      },
      {
        Header: t('tables.header-Name'),
        accessor: 'name',
        canSorter: false,
        Filter: DefaultColumnFilter,
      },
      {
        Header: t('tables.header-Severity'),
        accessor: 'severity',
        canSorter: false,
        Filter: SelectPriorityColumnFilter,
        Cell: ({ row, cell: { value } }) => <>{priorityTranslator(value)}</>
      },
      {
        Header: t('tables.header-Status'),
        accessor: 'status',
        canSorter: false,
        Filter: SelectColumnFilter,
      },
      {
        Header: t('tables.header-Decision'),
        accessor: 'l1_decision',
        canSorter: false,
        Filter: false,
        Cell: ({ row, cell: { value } }) => <RenderDecision value={value} />,
      },
      {
        Header: t('tables.header-Duration'),
        //accessor: 'stats.analysis_time',
        canSorter: false,
        Filter: false,  
        Cell: ({ row, cell: { value } }) => <DisplaySLA alert={row.original} />,

      },
    ],
    [t]
  )

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: {
        sortBy: [
          {
            id: 'createdAt',
            desc: true
          }
        ]
      }
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  return (
    <div className='tableFixHead' style={{ maxHeight: 'calc(100% - 50px)' }}>
      <table {...getTableProps()} >
        <thead>
          {headerGroups.map(headerGroup => (
            <tr key={headerGroup.id}{...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th key={column.id}{...column.canSorter ? column.getHeaderProps(column.getSortByToggleProps()) : null} >
                  <div className='th-div'>
                    {column.render('Header')}
                    <span className='sort-span'>
                      {column.canSorter
                        ? column.isSorted
                          ? column.isSortedDesc
                            ? <FaSortDown color='var(--table-th-filter-active)' />
                            : <FaSortUp color='var(--table-th-filter-active)' />
                          : < FaSort />
                        : ''
                      }
                    </span>
                    <span>{column.canFilter ? column.render('Filter') : null}</span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {(!data || data.length === 0)
            ? <p style={{ margin: '15px' }}>{t('adminPages.pendingWikisTable.info-noData')}</p>
            : rows.map(row => {
              prepareRow(row)
              return (
                <tr key={row.id}{...row.getRowProps()}>
                  {row.cells.map(cell => {
                    return (
                      <td key={cell.id}{...cell.getCellProps()} style={{ whiteSpace: 'normal' }} >
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          }
        </tbody>
      </table>
    </div>
  );
}

export default ClientAlerts;